import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import Can from '../../features/components/Can'
import Page from '../index'
import SimplePartnershipsContent from './SimplePartnershipsContent'
import AdvancedPartnershipsContent from './AdvancedPartnershipsContent'
import PartnershipRightsList from './PartnershipRightsList'
import ManageRightFiles from '../../forms/Multiplatform/Partnerships/ManageRightFiles'
import CreateRight from '../../forms/Multiplatform/Partnerships/RightForms/RightCreate'
import RightEdit from '../../forms/Multiplatform/Partnerships/RightForms/RightEdit'

import useIsAdvancedPartnership from '../../features/hooks/useIsAdvancedPartnership'

import { redirectTo } from '../../helpers/url'

import { rightsListSelector, rightsListWasLoadedSelector } from '../../modules/selectors/rights'
import { getCurrentUserSelector } from '../../modules/selectors/app'

import { RIGHT } from '../../constants/permissions'
import { RIGHT_TYPE_DIGITAL } from '../../forms/Multiplatform/Partnerships/RightForms/fields'
import { ROUTE_PARAMS, ROUTES } from '../../constants/routes'

import useSummaryPagesStyles from '../../styles/common/summaryPages'

function PartnershipsPage() {
  const summaryPagesClasses = useSummaryPagesStyles()

  const { t } = useTranslation()

  const { first_name: firstName } = useSelector(getCurrentUserSelector)
  const rights = useSelector(rightsListSelector)
  const rightsWasLoaded = useSelector(rightsListWasLoadedSelector)

  const rightsLength = rights.length

  const isAdvancedPartnership = useIsAdvancedPartnership()

  const isDigitalRightExist = !!rights.find(right => right['right_type'] === RIGHT_TYPE_DIGITAL)

  const breadcrumbs = useMemo(
    () => [
      {
        title: 'Partnership Dashboard'
      }
    ],
    []
  )

  const WelcomeMessage = (
    <h2 className={summaryPagesClasses.welcomeMessage}>
      👋 {t('Hello')}
      {firstName && `, ${firstName}`}. {t('What would you like to do?')}
    </h2>
  )

  const openLatestDigitalRight = useCallback(() => {
    // use right with latest start_date
    const latestDigitalRight = rights
      .filter(right => right['right_type'] === RIGHT_TYPE_DIGITAL)
      .sort((a, b) => new Date(b['start_date']).getTime() - new Date(a['start_date']).getTime())[0]

    redirectTo(ROUTES.rightsSummary, {
      [ROUTE_PARAMS.partnership]: latestDigitalRight['partnership_id'],
      [ROUTE_PARAMS.rightId]: latestDigitalRight.id
    })
  }, [rights])

  return (
    <Page
      helmetTitle="Partnerships"
      breadcrumbs={breadcrumbs}
      additionalPartnershipInfo={rightsWasLoaded ? `${rightsLength} rights` : ''}
      showPartnershipShortInfo
    >
      {isAdvancedPartnership ? (
        <AdvancedPartnershipsContent
          WelcomeMessage={WelcomeMessage}
          isDigitalRightExist={isDigitalRightExist}
          openLatestDigitalRight={openLatestDigitalRight}
        />
      ) : (
        <SimplePartnershipsContent
          WelcomeMessage={WelcomeMessage}
          isDigitalRightExist={isDigitalRightExist}
          openLatestDigitalRight={openLatestDigitalRight}
        />
      )}

      <PartnershipRightsList isAdvancedPartnership={isAdvancedPartnership} />
      {/*Forms: */}
      <Can I={'create'} a={RIGHT}>
        <CreateRight />
      </Can>
      <Can I={'update'} a={RIGHT}>
        <RightEdit />
      </Can>
      <ManageRightFiles />
    </Page>
  )
}

export default PartnershipsPage
